import { Fragment, useEffect, useState } from "react";
import { Loader2, FileText, Trash2, Info } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import api, { formatApiError } from "../api";

const fmtRp = (v) => `Rp ${Number(v || 0).toLocaleString("id-ID")}`;

export const SlipGaji = ({ canWrite = true }) => {
  const [supir, setSupir] = useState([]);
  const [slips, setSlips] = useState(null);
  const [nama, setNama] = useState("");
  const [periode, setPeriode] = useState(new Date().toISOString().slice(0, 7));
  const [generating, setGenerating] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = () => api.get("/gaji/slip").then(({ data }) => setSlips(data)).catch(() => setSlips([]));
  useEffect(() => {
    if (canWrite) api.get("/supir").then(({ data }) => setSupir(data)).catch(() => {});
    load();
  }, [canWrite]);

  const generate = async (e) => {
    e.preventDefault();
    setGenerating(true);
    try {
      const { data } = await api.post("/gaji/slip", { Nama_Supir: nama, Periode_Bulan: periode });
      toast.success(`Slip ${data.ID_Slip_Gaji} — ${data.Total_Trip_Selesai} trip, gaji bersih ${fmtRp(data.Gaji_Bersih_Diterima)}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  const remove = async (slip) => {
    try {
      await api.delete(`/gaji/slip/${slip.id}`);
      toast.success(`Slip ${slip.ID_Slip_Gaji} dihapus`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <Card className="border-zinc-200 shadow-sm">
      <CardHeader className="pb-4">
        <CardTitle className="text-base font-semibold tracking-tight flex items-center gap-2">
          <FileText className="w-4 h-4 text-emerald-700" /> Gaji Bulanan Supir (Slip)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {canWrite && (
        <form onSubmit={generate} className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end bg-zinc-50 border border-zinc-200 rounded-md p-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Nama Supir *</Label>
            <Select value={nama} onValueChange={setNama} required>
              <SelectTrigger data-testid="slip-supir-select" className="bg-white h-9"><SelectValue placeholder="Pilih supir" /></SelectTrigger>
              <SelectContent className="bg-white">
                {supir.map((s) => <SelectItem key={s.id} value={s.Nama_Supir}>{s.Nama_Supir}{s.Status_Supir !== "Aktif" ? ` (${s.Status_Supir})` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Periode Bulan *</Label>
            <Input data-testid="slip-periode-input" type="month" required value={periode} onChange={(e) => setPeriode(e.target.value)} className="bg-white h-9" />
          </div>
          <Button data-testid="generate-slip-button" type="submit" disabled={generating || !nama}
            className="bg-emerald-700 hover:bg-emerald-800 text-white transition-colors duration-200 h-9">
            {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
            Generate Slip
          </Button>
        </form>
        )}

        {slips === null ? (
          <div className="flex items-center justify-center py-10 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : slips.length === 0 ? (
          <p data-testid="slip-empty" className="text-sm text-zinc-400 text-center py-6">Belum ada slip gaji. Pilih supir dan bulan lalu klik "Generate Slip".</p>
        ) : (
          <div className="overflow-x-auto border border-zinc-200 rounded-md">
            <table className="w-full text-sm" data-testid="slip-table">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  {["ID Slip", "Supir", "Periode", "Trip Selesai", "Upah Kotor", "Potongan Susut", "Gaji Bersih", "Aksi"].map((h) => (
                    <th key={h} className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-zinc-500 font-medium whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {slips.map((s) => (
                  <Fragment key={s.id}>
                    <tr data-testid={`slip-row-${s.ID_Slip_Gaji}`} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors duration-200">
                      <td className="px-4 py-2.5 font-medium text-zinc-900 whitespace-nowrap">{s.ID_Slip_Gaji}</td>
                      <td className="px-4 py-2.5 whitespace-nowrap">{s.Nama_Supir}</td>
                      <td className="px-4 py-2.5 whitespace-nowrap">{s.Periode_Bulan}</td>
                      <td className="px-4 py-2.5 tabular-nums text-center">{s.Total_Trip_Selesai}</td>
                      <td className="px-4 py-2.5 tabular-nums whitespace-nowrap">{fmtRp(s.Total_Upah_Kotor)}</td>
                      <td className="px-4 py-2.5 tabular-nums whitespace-nowrap text-red-700">
                        <span className="inline-flex items-center gap-1.5">
                          {fmtRp(s.Potongan_Klaim_Susut)}
                          {s.Detail_Potongan?.length > 0 && (
                            <button data-testid={`slip-detail-toggle-${s.ID_Slip_Gaji}`} type="button" onClick={() => setDetailId(detailId === s.id ? null : s.id)}
                              className="p-0.5 rounded text-red-500 hover:bg-red-50 transition-colors duration-200" title="Rincian potongan">
                              <Info className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </span>
                      </td>
                      <td className={`px-4 py-2.5 tabular-nums whitespace-nowrap font-semibold ${s.Gaji_Bersih_Diterima < 0 ? "text-red-700" : "text-emerald-800"}`}>
                        {fmtRp(s.Gaji_Bersih_Diterima)}
                      </td>
                      <td className="px-4 py-2.5">
                        {canWrite && (
                        <button data-testid={`slip-delete-${s.ID_Slip_Gaji}`} onClick={() => remove(s)}
                          className="p-1.5 rounded-md text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors duration-200" title="Hapus">
                          <Trash2 className="w-4 h-4" />
                        </button>
                        )}
                      </td>
                    </tr>
                    {detailId === s.id && (
                      <tr key={`${s.id}-detail`} data-testid={`slip-detail-${s.ID_Slip_Gaji}`} className="bg-red-50/60 border-b border-zinc-100">
                        <td colSpan={8} className="px-6 py-3">
                          <p className="text-xs font-semibold text-red-900 mb-1.5">Rincian Potongan Klaim Susut:</p>
                          <ul className="space-y-1">
                            {s.Detail_Potongan.map((d, i) => (
                              <li key={i} className="text-xs text-red-800">
                                {d.ID_Trip} ({d.Jenis_Komoditas}): susut {Math.abs(d.Penyusutan_KG)} KG − toleransi {d.Toleransi_KG} KG = selisih {d.Selisih_KG} KG × {fmtRp(d.Harga_Per_KG)}/KG = <b>{fmtRp(d.Denda_Rp)}</b>
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
